import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { SprintCreateForm } from "./sprint-form";
import { ErrorBanner, RequiredMark, Skeleton, useConfirm } from "./ui-kit";

type SprintTask = {
  assigneeName: string;
  estimatePoints: number | null;
  id: string;
  status: string;
  taskKey: string;
  title: string;
};

type Capacity = {
  assignedPoints: number;
  capacityPoints: number | null;
  displayName: string;
  userId: string;
  variancePoints: number | null;
};

type Sprint = {
  capacities: Capacity[];
  completedAt: string | null;
  completedPoints: number;
  completedTaskCount: number;
  endsOn: string | null;
  goal: string | null;
  id: string;
  name: string;
  startsOn: string | null;
  state: "planned" | "active" | "completed" | "cancelled";
  taskCount: number;
  totalPoints: number;
};

type SprintData = {
  permissions: string[];
  sprints: Sprint[];
  team: { deliveryMode: "scrum" | "kanban" };
};

type SprintAutomation = {
  anchorDate: string;
  dayOfWeek: number;
  enabled: boolean | number;
  intervalWeeks: number;
  lastCompletedSprintId: string | null;
  lastCreatedSprintId: string | null;
  lastFiredAt: string | null;
  lastFiredPeriodStart: string | null;
  timeOfDay: string;
  timezone: string;
};

const WEEKDAY_OPTIONS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const INTERVAL_OPTIONS = Array.from({ length: 12 }, (_, index) => index + 1);

const api = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "Taskei could not complete that request.");
  return payload;
};

const capacityMessage = (capacity: Capacity) => {
  if (capacity.capacityPoints === null) return { className: "unset", text: "Not set" };
  const difference = capacity.assignedPoints - capacity.capacityPoints;
  if (difference > 0) return { className: "over", text: `Over by ${difference} pt` };
  if (difference < 0) return { className: "under", text: `Under by ${Math.abs(difference)} pt` };
  return { className: "balanced", text: "At capacity" };
};

function SprintStoryList({ sprintId, teamId }: { sprintId: string; teamId: string }) {
  const [tasks, setTasks] = useState<SprintTask[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setTasks(null);
    setError("");
    api<{ tasks: SprintTask[] }>(`/api/tasks?teamId=${encodeURIComponent(teamId)}&sprintId=${encodeURIComponent(sprintId)}`)
      .then((result) => { if (!cancelled) setTasks(result.tasks); })
      .catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not load sprint stories."); });
    return () => { cancelled = true; };
  }, [sprintId, teamId]);
  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!tasks) return <Skeleton label="Loading stories…" />;
  if (!tasks.length) return <p className="sprint-empty">No stories are assigned to this sprint yet.</p>;
  return (
    <div className="simple-list" aria-label="Stories in this sprint">
      {tasks.map((task) => (
        <article key={task.id}>
          <div><strong>{task.taskKey}</strong> {task.title}</div>
          <small>{task.status.replace("_", " ")} · {task.assigneeName}{task.estimatePoints !== null ? ` · ${task.estimatePoints} pt` : ""}</small>
        </article>
      ))}
    </div>
  );
}

export function SprintsPage({
  isAdmin,
  onSelectSprint,
  selectedSprintId,
  teamId,
}: {
  isAdmin: boolean;
  onSelectSprint: (sprintId: string | null) => void;
  selectedSprintId: string | null;
  teamId: string;
}) {
  const confirm = useConfirm();
  const [data, setData] = useState<SprintData | null>(null);
  const [automation, setAutomation] = useState<SprintAutomation | null>(null);
  const [automationBusy, setAutomationBusy] = useState(false);
  const [automationError, setAutomationError] = useState("");
  const timezones = useMemo(() => Intl.supportedValuesOf("timeZone"), []);
  const [busy, setBusy] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setData(await api<SprintData>(`/api/sprints?teamId=${encodeURIComponent(teamId)}`));
  }, [teamId]);
  useEffect(() => { void load().catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load sprints.")); }, [load]);
  useEffect(() => {
    api<SprintAutomation>(`/api/teams/${encodeURIComponent(teamId)}/sprint-automation`)
      .then(setAutomation)
      .catch(() => { /* non-fatal -- the settings form just stays hidden if this fails */ });
  }, [teamId]);

  const saveAutomation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!automation) return;
    setAutomationBusy(true);
    setAutomationError("");
    try {
      await api(`/api/teams/${encodeURIComponent(teamId)}/sprint-automation`, {
        body: JSON.stringify(automation),
        method: "PATCH",
      });
    } catch (reason) {
      setAutomationError(reason instanceof Error ? reason.message : "Could not save the schedule.");
    } finally {
      setAutomationBusy(false);
    }
  };

  const mutate = async (key: string, action: () => Promise<unknown>) => {
    setBusy(key); setError("");
    try { await action(); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Could not update sprint planning."); }
    finally { setBusy(""); }
  };
  const createSprint = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    await mutate("create", () => api("/api/sprints", {
      body: JSON.stringify({
        endsOn: form.get("endsOn"), goal: form.get("goal"), name: form.get("name"),
        startsOn: form.get("startsOn"), teamId,
      }),
      method: "POST",
    }));
    formElement.reset(); setCreating(false);
  };
  if (!data) return error ? <ErrorBanner>{error}</ErrorBanner> : <Skeleton label="Loading sprints…" variant="list" />;
  const canManageSprints = isAdmin || data.permissions.includes("manage_sprints");
  const canManageBoard = isAdmin || data.permissions.includes("manage_board");
  const planned = data.sprints.filter(({ state }) => state === "planned");
  return (
    <section className="sprints-page">
      <div className="page-actions">
        <label>Method<select aria-label="Delivery method" disabled={!canManageBoard || Boolean(busy)} onChange={(event) => void mutate("mode", () => api(`/api/teams/${teamId}/delivery-mode`, { body: JSON.stringify({ deliveryMode: event.target.value }), method: "PATCH" }))} value={data.team.deliveryMode}><option value="scrum">Scrum</option><option value="kanban">Kanban</option></select></label>
        {data.team.deliveryMode === "scrum" && canManageSprints && <button className="button primary" onClick={() => setCreating((value) => !value)} type="button">{creating ? "Cancel" : "+ Create sprint"}</button>}
      </div>
      {canManageSprints && data.team.deliveryMode === "scrum" && automation && (
        <form className="sprint-automation-form" onSubmit={(event) => void saveAutomation(event)}>
          <div className="section-heading"><div><p className="eyebrow">Automation</p><h2>Auto-create the next sprint</h2></div></div>
          <ErrorBanner>{automationError}</ErrorBanner>
          <label className="sprint-automation-toggle">
            <input
              checked={Boolean(automation.enabled)}
              onChange={(event) => setAutomation({ ...automation, enabled: event.target.checked })}
              type="checkbox"
            />
            Auto-create the next sprint on a schedule
          </label>
          <p className="admin-help">
            When this fires, it also auto-completes the current active sprint and carries its incomplete tasks into
            the new sprint — with nobody reviewing it first.
          </p>
          <label>Day of week<select onChange={(event) => setAutomation({ ...automation, dayOfWeek: Number(event.target.value) })} value={automation.dayOfWeek}>
            {WEEKDAY_OPTIONS.map((name, index) => <option key={name} value={index}>{name}</option>)}
          </select></label>
          <label>Repeats<select onChange={(event) => setAutomation({ ...automation, intervalWeeks: Number(event.target.value) })} value={automation.intervalWeeks}>
            {INTERVAL_OPTIONS.map((weeks) => <option key={weeks} value={weeks}>{weeks === 1 ? "Every week" : `Every ${weeks} weeks`}</option>)}
          </select></label>
          <label>Time of day<input onChange={(event) => setAutomation({ ...automation, timeOfDay: event.target.value })} type="time" value={automation.timeOfDay} /></label>
          <label>Timezone<select onChange={(event) => setAutomation({ ...automation, timezone: event.target.value })} value={automation.timezone}>
            {/* UTC is a legal Intl timeZone value but isn't part of the canonical
                IANA enumeration Intl.supportedValuesOf returns (that list is real
                zones like America/New_York, not UTC/GMT-style aliases) -- listed
                explicitly since it's a common, sensible choice on its own. */}
            <option value="UTC">UTC</option>
            {timezones.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
          </select></label>
          <button className="button primary" disabled={automationBusy} type="submit">{automationBusy ? "Saving…" : "Save schedule"}</button>
        </form>
      )}
      <div className="section-heading"><div><p className="eyebrow">Delivery history</p><h2>All sprints</h2><p className="section-description">Plan capacity, manage sprint status, and keep future reports together.</p></div></div>
      <ErrorBanner>{error}</ErrorBanner>
      {creating && <SprintCreateForm busy={busy === "create"} onSubmit={(event) => void createSprint(event)} />}
      {data.team.deliveryMode === "kanban" ? <div className="empty-state compact"><h2>Sprints are disabled</h2><p>Switch the team to Scrum to plan time-boxed work.</p></div> : data.sprints.length ? (
        <div className="sprint-directory">
          {data.sprints.map((sprint) => {
            const selected = sprint.id === selectedSprintId;
            return <article className={`sprint-directory-card sprint-${sprint.state}${selected ? " selected" : ""}`} key={sprint.id}>
              <header>
                <div><span className={`sprint-state ${sprint.state}`}>{sprint.state}</span><h3>{sprint.name}</h3><p>{sprint.goal || "No sprint goal has been set."}</p></div>
                <div className="sprint-card-actions">
                  <button className="button secondary" onClick={() => onSelectSprint(selected ? null : sprint.id)} type="button">{selected ? "Collapse" : "View sprint"}</button>
                  {canManageSprints && <button aria-label={`Delete ${sprint.name}`} className="button danger" disabled={Boolean(busy)} onClick={async () => {
                    const ok = await confirm({
                      description: `${sprint.taskCount} assigned task${sprint.taskCount === 1 ? "" : "s"} will return to the backlog. This cannot be undone.`,
                      title: `Delete ${sprint.name}?`,
                      tone: "danger",
                    });
                    if (!ok) return;
                    if (selected) onSelectSprint(null);
                    void mutate(`delete-${sprint.id}`, () => api(`/api/sprints/${sprint.id}`, { method: "DELETE" }));
                  }} type="button">Delete</button>}
                </div>
              </header>
              <dl className="sprint-summary"><div><dt>Dates</dt><dd>{sprint.startsOn || "Not set"} – {sprint.endsOn || "Not set"}</dd></div><div><dt>Tasks</dt><dd>{sprint.completedTaskCount}/{sprint.taskCount}</dd></div><div><dt>Points</dt><dd>{sprint.completedPoints}/{sprint.totalPoints}</dd></div></dl>
              {selected && <div className="sprint-expanded">
                <section><div className="subsection-heading"><div><h4>Stories in this sprint</h4><p>Every task and story currently assigned to {sprint.name}.</p></div></div>
                  <SprintStoryList sprintId={sprint.id} teamId={teamId} />
                </section>
                <section><div className="subsection-heading"><div><h4>Developer capacity</h4><p>Assigned estimates compared with optional sprint capacity.</p></div></div>
                  <form onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); void mutate(`capacity-${sprint.id}`, () => api(`/api/sprints/${sprint.id}/capacities`, { body: JSON.stringify({ capacities: sprint.capacities.map(({ userId }) => ({ capacityPoints: form.get(`capacity-${userId}`) === "" ? null : Number(form.get(`capacity-${userId}`)), userId })) }), method: "PUT" })); }}>
                    <div className="capacity-table"><div className="capacity-header"><span>Developer</span><span>Assigned</span><span>Capacity</span><span>Balance</span></div>{sprint.capacities.map((capacity) => { const message = capacityMessage(capacity); return <div className="capacity-row" key={capacity.userId}><strong>{capacity.displayName}</strong><span>{capacity.assignedPoints} pt</span><input aria-label={`Capacity for ${capacity.displayName} in ${sprint.name}`} defaultValue={capacity.capacityPoints ?? ""} disabled={!canManageSprints} min="0" name={`capacity-${capacity.userId}`} placeholder="Optional" step="0.5" type="number" /><span className={`capacity-flag ${message.className}`}>{message.text}</span></div>; })}</div>
                    {canManageSprints && <button className="button secondary" disabled={busy === `capacity-${sprint.id}`} type="submit">{busy === `capacity-${sprint.id}` ? "Saving…" : "Save capacity"}</button>}
                  </form>
                </section>
                <section className="sprint-reports"><h4>Reports</h4><p>Sprint reports will appear here as reporting capabilities are added.</p></section>
                {canManageSprints && <div className="sprint-lifecycle-actions">
                  {sprint.state === "planned" && <button className="button secondary" disabled={Boolean(busy)} onClick={() => void mutate(sprint.id, () => api(`/api/sprints/${sprint.id}/start`, { method: "POST" }))} type="button">Start sprint</button>}
                  {sprint.state === "active" && <label>Move unfinished work to<select aria-label={`Carryover destination for ${sprint.name}`} defaultValue=""><option value="">Backlog</option>{planned.map((candidate) => candidate.id !== sprint.id && <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</select><button className="button secondary" disabled={Boolean(busy)} onClick={(event) => { const select = event.currentTarget.parentElement?.querySelector("select"); void mutate(sprint.id, () => api(`/api/sprints/${sprint.id}/complete`, { body: JSON.stringify({ destinationSprintId: select?.value || null }), method: "POST" })); }} type="button">Complete sprint</button></label>}
                  {sprint.state === "completed" && <button className="button secondary" disabled={Boolean(busy)} onClick={() => void mutate(sprint.id, () => api(`/api/sprints/${sprint.id}/reopen`, { method: "POST" }))} type="button">Reopen sprint</button>}
                  {!['completed', 'cancelled'].includes(sprint.state) && <details><summary>Edit sprint</summary><form className="sprint-edit-form" onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); void mutate(`edit-${sprint.id}`, () => api(`/api/sprints/${sprint.id}`, { body: JSON.stringify({ endsOn: form.get("endsOn"), goal: form.get("goal"), name: form.get("name"), startsOn: form.get("startsOn") }), method: "PATCH" })); }}><label><span>Name<RequiredMark /></span><input defaultValue={sprint.name} name="name" required /></label><label>Goal<input defaultValue={sprint.goal ?? ""} name="goal" /></label><label>Starts<input defaultValue={sprint.startsOn ?? ""} name="startsOn" type="date" /></label><label>Ends<input defaultValue={sprint.endsOn ?? ""} name="endsOn" type="date" /></label><button className="button secondary" type="submit">Save sprint</button></form></details>}
                </div>}
              </div>}
            </article>;
          })}
        </div>
      ) : <div className="empty-state"><h2>No sprints yet</h2><p>Create a sprint to start planning team capacity and delivery.</p></div>}
    </section>
  );
}
